import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, Badge, Button, CodeDisplay, Icon, Switch, Tabs, DataTable, Tooltip } from "@aidkitorg/component-library";
import { Notification } from "@aidkitorg/types/lib/survey";
import { useMemo, useState } from "react";
import { SupportedLanguage } from "../Context";
import { langToWord } from '../Localization';
import { LanguageNames, RtlLanguages } from "@aidkitorg/i18n/lib";
import ExploreLink from "../Components/ExploreLink";
import * as v0 from "@aidkitorg/types/lib/survey";
import { renderLinks } from "../Util";

function htmlToPlainText(html: string): string {
  if (!html?.trimStart().startsWith('<')) return html;
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6]|blockquote|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n').trim();
}
import type { NotificationRecipientsResult } from "@aidkitorg/aidkit/lib/notifications/NotificationService";
import { ColumnDef } from "@tanstack/react-table";

export function Stats(props: { 
  stats: { name: string, stat: string,  exploreLink?: Parameters<typeof ExploreLink>[0] }[],
}) {
  return (
    <div>
      <dl className="ml-2 mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
        {props.stats.map((item) => (
          <div key={item.name} className="overflow-hidden rounded-lg bg-white px-4 py-2 shadow sm:p-6">
            <dt className="break-words text-sm font-medium text-gray-500">{item.name}</dt>
            <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{item.stat}</dd>
            {item.exploreLink && (
              <ExploreLink {...item.exploreLink} />
            )}
          </div>
        ))}
      </dl>
    </div>
  )
}

export function DetailList({ details }: { details: Record<string, string | React.ReactNode> }) {
  return <div>
    <div className="mt-6 border-t border-gray-100">
      <dl className="divide-y divide-gray-100">
        {Object.entries(details).map(([key, value]) =>
          <div key={key} className="hover:bg-gray-100 rounded-md p-1 items-center grid grid-flow-col-dense grid-cols-2">
            <dt className="text-sm/6 font-medium text-gray-900">{key}</dt>
            <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 items-center justify-end">{value}</dd>
          </div>
        )}
      </dl>
    </div>
  </div>
}

export function SqlViewer({ sql }: { sql: string }) {
  const [showSql, setShowSql] = useState(false);

  return <div className="space-y-2">
    <Switch label="Show SQL" onCheckedChange={setShowSql} />
    <div hidden={!showSql} className="pb-5">
      <CodeDisplay code={sql} showLineNumbers copyable language="sql" />
    </div>
  </div>
}

export function NotificationSimulation({ task, simulationSource, enableEmailMarkdown, simulated }: {
  task: v0.Notification,
  simulationSource: "simulation" | "query" | "eligibility" | null,
  enableEmailMarkdown: boolean | undefined,
  simulated: Record<string, Array<{ 
    error: string,
    langAttempted: SupportedLanguage | null,
  } | {
    emailContent: string, 
    smsContent: string, 
    langAttempted: SupportedLanguage | null, 
    emailLangResult: SupportedLanguage,
    smsLangResult: SupportedLanguage }>> | null 
}) {
  const { testUIDs, contactMethod } = task;

  const behaviorIfLangIsNotDefined = task.recipient === 'Unsubmitted Applicant' ? { 
    message: 'will not send'
  } : {
    message: 'will default to English'
  };

  if (simulated == null) return null;
  return useMemo(() => <>{
    (!Object.keys(simulated).length && simulationSource === "simulation") ? <div className="text-red-600">Configure Test UIDs to simulate</div> :
      <Accordion type="single" dir="ltr" defaultValue={'simulated-messages'}>
        <AccordionItem value="simulated-messages">
          <AccordionTrigger>
            <h3 className="text-xl text-left">Simulated Messages for {Object.keys(simulated || {}).length} applicant(s) {task.testUIDs?.length ? <span className="text-green-800">from the provided Test UIDs</span> : <span className="text-yellow-800">from the first applicant that matched the Check Query</span>}</h3>
          </AccordionTrigger>
          <AccordionContent>
            {Object.keys(simulated || {}).map((uid) => {
              // Gather unique errors and display them
              // TODO: These error codes hold more info, like unsuccessfulReplacements.
              const uniqueErrors: { 
                [code: string]: { 
                  error: string, 
                  langAttempted: SupportedLanguage | null 
                } 
              } = {};
              (simulated[uid] || []).forEach((sim) => {
                if ('error' in sim) {
                  uniqueErrors[sim.error] = sim;
                }
              });


              return <div key={uid}>
                <h4 className="text-lg flex flex-row gap-2">
                  <span className="font-bold text-md">Applicant:</span> 
                  <span>{uid}</span>
                  {task.testUIDs?.length ? <Badge variant="info">Provided Test UID</Badge> : <Badge variant="warning">Random Eligible Applicant</Badge>}
                </h4>
                {Object.values(uniqueErrors).map((error) => {
                  return <div key={error.error}>
                    <Alert variant="warning" iconName="ExclamationTriangleIcon">
                      <span>This applicant had an error simulating their notification.</span><br/>
                      <span className="font-medium">Error: </span><span>{error.error}</span>
                    </Alert>
                  </div>
                })}
                {(simulated[uid] || []).map((sim) => { 
                  if ('error' in sim) {
                    return null;
                  }
                  const alertsToShow: string[] = [];
                  if (!!sim.langAttempted && sim.langAttempted !== sim.smsLangResult) {
                    alertsToShow.push('SMS');
                  }
                  if (!!sim.langAttempted && sim.langAttempted !== sim.emailLangResult) {
                    alertsToShow.push('Email');
                  }
                  const joinedAlerts = alertsToShow.join(' and ');
                  return <div key={sim.langAttempted}>
                    {sim.langAttempted && 
                    <h4 className="text-lg font-bold">Attempted to Simulate: {sim.langAttempted + ' (' + LanguageNames[sim.langAttempted]+ ')'}</h4>}
                    {Boolean(!!sim.langAttempted && alertsToShow.length) ? 
                      <Alert variant="danger">Warning: {LanguageNames[sim.langAttempted!]} is not in the configured Notification {joinedAlerts} Content. If an applicant's preferred language is {LanguageNames[sim.langAttempted!]}, the {joinedAlerts} notification {behaviorIfLangIsNotDefined.message}.</Alert> : null}
                    {(sim.langAttempted === null || (sim.emailLangResult === sim.langAttempted)) && <>
                      {contactMethod !== 'sms' && contactMethod !== 'whatsapp' && (() => {
                        const isRtl = RtlLanguages.includes(sim.emailLangResult);
                        return <>
                          <div>Email:</div>
                          {(enableEmailMarkdown || sim.emailContent?.trimStart().startsWith('<'))
                            ? <div dir={isRtl ? 'rtl' : 'ltr'} className={`border rounded-xl px-3 markdown ${isRtl ? 'text-right' : ''}`} dangerouslySetInnerHTML={{ __html: sim.emailContent }}></div>
                            : <pre dir={isRtl ? 'rtl' : 'ltr'} className={`border rounded-xl px-3 whitespace-pre-wrap ${isRtl ? 'text-right' : ''}`} dangerouslySetInnerHTML={{ __html: sim.emailContent }}></pre>}
                        </>;
                      })()}
                    </>}
                    {(sim.langAttempted === null || (sim.smsLangResult === sim.langAttempted)) && <>
                      {contactMethod !== 'email' && (() => {
                        const isRtl = RtlLanguages.includes(sim.smsLangResult);
                        return <>
                          <div>{contactMethod === 'whatsapp' ? 'WhatsApp:' : contactMethod === 'sms' ? 'SMS:' : 'SMS/WhatsApp:'}</div>
                          <pre dir={isRtl ? 'rtl' : 'ltr'} className={`mb-5 border rounded-xl px-3 whitespace-pre-wrap ${isRtl ? 'text-right' : ''}`}>{renderLinks(htmlToPlainText(sim.smsContent))}</pre>
                        </>;
                      })()}
                    </>}</div>
                })}
              </div>
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      // Do not update when contactMethod is changed, only when simulated changes.
      // This avoids falsely appearing like a re-simulation has occurred when it hasn't.
  }</>, [simulated]);
}

type GroupedRecipient = NotificationRecipientsResult['eligible'][number] & {
  channels: ('email' | 'sms' | 'whatsapp')[];
  subRows?: GroupedRecipient[];
}

function groupByUid(rows: NotificationRecipientsResult['eligible'][number][]): GroupedRecipient[] {
  const grouped = new Map<string, NotificationRecipientsResult['eligible'][number][]>();
  for (const row of rows) {
    const existing = grouped.get(row.uid);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(row.uid, [row]);
    }
  }
  return Array.from(grouped.values()).map(uidRows => {
    const first = uidRows[0];
    const channels = uidRows.map(r => r.channel);
    return {
      ...first,
      channels,
      subRows: uidRows.length > 1 ? uidRows.map(r => ({ ...r, channels: [r.channel] })) : undefined,
    };
  });
}

function MessageCell({ row }: { row: { depth: number, original: GroupedRecipient } }) {
  if (row.depth === 0 && row.original.subRows && row.original.subRows.length > 0) {
    return <span className="text-gray-400">--</span>;
  }
  const message = row.original.message || '--';
  const subject = row.original.subject;
  return (
    <Tooltip text={message}>
      <div className="max-w-xs cursor-help">
        {subject && (
          <div className="text-xs text-gray-500 mb-1">
            <span className="font-medium">Subject:</span> {subject}
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words line-clamp-2">
          {message}
        </div>
      </div>
    </Tooltip>
  );
}

export function RecipientsTable({ eligible, sent, variables, recipientsTab, setRecipientsTab }: {
  eligible: NotificationRecipientsResult['eligible'],
  sent: NotificationRecipientsResult['sent'],
  variables: string[],
  recipientsTab: 'sent' | 'eligible',
  setRecipientsTab: (tab: 'sent' | 'eligible') => void
}) {
  const groupedEligible = useMemo(() => groupByUid(eligible), [eligible]);
  const groupedSent = useMemo(() => groupByUid(sent), [sent]);

  const eligibleColumns = useMemo((): ColumnDef<GroupedRecipient>[] => {
    const cols: ColumnDef<GroupedRecipient>[] = [
      {
        accessorKey: 'uid',
        header: 'UID',
        cell: ({ row }) => {
          if (row.depth > 0) return null;
          return <div className="flex items-center gap-1">
            {row.original.subRows && row.original.subRows.length > 0 && (
              <Button className="px-0" onClick={row.getToggleExpandedHandler()} variant="unstyled" title={row.getIsExpanded() ? 'Collapse' : 'Expand'}>
                {row.getIsExpanded() ? <Icon name="ChevronDownIcon" /> : <Icon name="ChevronRightIcon" />}
              </Button>
            )}
            <CodeDisplay displayMode="inline" code={String(row.original.uid)} copyable className="text-xs" />
          </div>;
        }
      },
      {
        accessorKey: 'legal_name',
        header: 'Name',
        cell: ({ row }) => row.depth > 0 ? null : (row.original.legal_name || '--')
      },
      {
        accessorKey: 'channel',
        header: 'Channel',
        cell: ({ row }) => {
          if (row.depth > 0) return <span className="capitalize">{row.original.channel}</span>;
          return <span className="capitalize">{row.original.channels.join(', ')}</span>;
        }
      },
    ];

    variables.sort().forEach(variable => {
      cols.push({
        accessorFn: (row) => row.variables?.[variable],
        id: `variable_${variable}`,
        header: variable,
        cell: ({ row }) => {
          if (row.depth > 0) return null;
          const value = row.original.variables?.[variable];
          if (value === undefined || value === null || value === '') {
            return <span className="text-gray-400 italic">--</span>;
          }
          return <span className="text-xs">{value}</span>;
        }
      });
    });

    cols.push({
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => <MessageCell row={row} />
    });

    return cols;
  }, [variables]);

  const sentColumns = useMemo((): ColumnDef<GroupedRecipient>[] => {
    const cols: ColumnDef<GroupedRecipient>[] = [
      {
        accessorKey: 'uid',
        header: 'UID',
        cell: ({ row }) => {
          if (row.depth > 0) return null;
          return <div className="flex items-center gap-1">
            {row.original.subRows && row.original.subRows.length > 0 && (
              <Button className="px-0" onClick={row.getToggleExpandedHandler()} variant="unstyled" title={row.getIsExpanded() ? 'Collapse' : 'Expand'}>
                {row.getIsExpanded() ? <Icon name="ChevronDownIcon" /> : <Icon name="ChevronRightIcon" />}
              </Button>
            )}
            <CodeDisplay displayMode="inline" code={String(row.original.uid)} copyable className="text-xs" />
          </div>;
        }
      },
      {
        accessorKey: 'legal_name',
        header: 'Name',
        cell: ({ row }) => row.depth > 0 ? null : (row.original.legal_name || '--')
      },
      {
        accessorKey: 'channel',
        header: 'Channel',
        cell: ({ row }) => {
          if (row.depth > 0) return <span className="capitalize">{row.original.channel}</span>;
          return <span className="capitalize">{row.original.channels.join(', ')}</span>;
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          if (row.depth === 0 && row.original.subRows && row.original.subRows.length > 0) return null;
          const status = row.original.status;
          const variant = status === 'sent' ? 'success' : status === 'failed' ? 'error' : 'warning';
          return <Badge variant={variant}>{status}</Badge>;
        }
      },
    ];

    variables.sort().forEach(variable => {
      cols.push({
        accessorFn: (row) => row.variables?.[variable],
        id: `variable_${variable}`,
        header: variable,
        cell: ({ row }) => {
          if (row.depth > 0) return null;
          const value = row.original.variables?.[variable];
          if (value === undefined || value === null || value === '') {
            return <span className="text-gray-400 italic">--</span>;
          }
          return <span className="text-xs">{value}</span>;
        }
      });
    });

    cols.push({
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => <MessageCell row={row} />
    });

    return cols;
  }, [variables]);

  return (
    <Tabs
      justify="left"
      selectedTab={recipientsTab}
      onChange={(tab) => setRecipientsTab(tab as 'sent' | 'eligible')}
      tabs={[
        {
          key: 'eligible',
          name: `Yet to Receive (${groupedEligible.length})`,
          content: (
            <DataTable
              columns={eligibleColumns}
              data={groupedEligible}
              getSubRows={(row) => row.subRows ?? []}
              pageSize={5}
              enableSearch
              searchLabel="Search"
              noDataMessage="No eligible recipients"
            />
          )
        },
        {
          key: 'sent',
          name: `Received (${groupedSent.length})`,
          content: (
            <DataTable
              columns={sentColumns}
              data={groupedSent}
              getSubRows={(row) => row.subRows ?? []}
              pageSize={5}
              enableSearch
              searchLabel="Search"
              noDataMessage="No sent notifications"
            />
          )
        }
      ]}
    />
  );
}
